import { useState } from "react"
import dayjs from "dayjs"
import { useSmoothApi } from "src/hooks/useSmoothApi"
import { SMOOTH_LINE_API_URL } from "src/lib/constants"
import type { DunningCaseItem } from "../types"

const promisesBase = (id: number) =>
  `${SMOOTH_LINE_API_URL}/base_api/smoother/screen/dunning_case_detail/${id}/payment_promises`

export type PaymentPromiseInput = {
  /** datetime-local value ("YYYY-MM-DDTHH:mm"); sent to the API as ISO8601. */
  scheduledAtLocal: string
  /** 任意。空文字は送らない */
  amount: string
  note: string
}

const toPayload = (input: PaymentPromiseInput) => {
  const note = input.note.trim()
  const amount = input.amount.trim() === "" ? null : Number(input.amount)
  return {
    scheduledAt: dayjs(input.scheduledAtLocal).toISOString(),
    // Omit empty optionals so we never persist empty values.
    ...(amount != null && Number.isFinite(amount) ? { amount } : {}),
    ...(note ? { note } : {}),
  }
}

/**
 * 督促所有の支払約束（dunning_payment_promises）の登録・取消。
 * 登録すると同一ケースの既存有効約束はサーバー側で superseded に置換される。
 * 各操作は更新後のケース詳細一式を返すので、呼び出し側で state を差し替える。
 */
export const useDunningPaymentPromises = (id: number) => {
  const { postWithToken, patchWithToken } = useSmoothApi()
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const run = async <T>(fn: () => Promise<T>): Promise<T | undefined> => {
    setIsSubmitting(true)
    setError(null)
    try {
      return await fn()
    } catch (e) {
      const message =
        (e as { response?: { data?: { message?: string } } })?.response?.data?.message ??
        "操作に失敗しました。"
      setError(message)
      return undefined
    } finally {
      setIsSubmitting(false)
    }
  }

  const registerPromise = (input: PaymentPromiseInput) =>
    run(async () => {
      const res = await postWithToken(promisesBase(id), toPayload(input))
      return res.data as DunningCaseItem
    })

  const cancelPromise = (promiseId: number) =>
    run(async () => {
      const res = await patchWithToken(`${promisesBase(id)}/${promiseId}/cancel`)
      return res.data as DunningCaseItem
    })

  const clearError = () => setError(null)

  return { registerPromise, cancelPromise, isSubmitting, error, clearError }
}
